import { useEffect, useState } from "react"
import { AxiosError } from "axios"
import { ColumnDef } from "@tanstack/react-table"
import { getPartnerAccruals, IPartnerAccrual } from "@/shared/api/partners"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import { PageSubtitle } from "@/shared/ui/page-subtitle"
import { Table } from "@/shared/ui/table"
import { PageHeader } from "@/widgets/page-header"

const renderStringList = (items: string[]) => items.length ? items.join(', ') : "—"

const renderNullableBoolean = (value: boolean | null) => {
    if (value === null) return "—"
    return value ? "да" : "нет"
}

export const PartnerAccrualsPage = () => {
    const [items, setItems] = useState<IPartnerAccrual[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [emptyLabel, setEmptyLabel] = useState("Нет данных о начислениях")

    const columns: ColumnDef<IPartnerAccrual, any>[] = [
        {
            accessorKey: 'name',
            id: 'name',
            enableSorting: false,
            cell: info => info.getValue() || "—",
            header: () => <div className="!text-start text-nowrap">Название</div>,
        },
        {
            accessorKey: 'bonus_name',
            id: 'bonus_name',
            enableSorting: false,
            cell: info => info.getValue() || "—",
            header: () => <div className="!text-start text-nowrap">Бонус</div>,
        },
        {
            accessorKey: 'partner_name',
            id: 'partner_name',
            enableSorting: false,
            cell: info => info.getValue() || "—",
            header: () => <div className="!text-start text-nowrap">Партнёр</div>,
        },
        {
            accessorKey: 'accrual_type',
            id: 'accrual_type',
            enableSorting: false,
            cell: info => info.getValue() ?? "—",
            header: () => <div className="!text-start text-nowrap">Тип начисления</div>,
        },
        {
            accessorKey: 'accrual_value',
            id: 'accrual_value',
            enableSorting: false,
            cell: info => info.getValue() ?? "—",
            header: () => <div className="!text-start text-nowrap">Значение</div>,
        },
        {
            accessorKey: 'priority',
            id: 'priority',
            enableSorting: false,
            cell: info => info.getValue() ?? "—",
            header: () => <div className="!text-start text-nowrap">Приоритет</div>,
        },
        {
            accessorFn: (item) => renderStringList(item.allowed_oem_groups),
            id: 'allowed_oem_groups',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Разрешённые OEM-группы</div>,
        },
        {
            accessorFn: (item) => renderStringList(item.restricted_oem_groups),
            id: 'restricted_oem_groups',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Запрещённые OEM-группы</div>,
        },
        {
            accessorFn: (item) => renderNullableBoolean(item.attracted_clients_only),
            id: 'attracted_clients_only',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Только привлечённые по ссылке</div>,
        },
        {
            accessorFn: (item) => item.max_accruals_per_client === null ? "безлимитно" : item.max_accruals_per_client,
            id: 'max_accruals_per_client',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Лимит на клиента</div>,
        },
    ]

    useEffect(() => {
        const load = async () => {
            setIsLoading(true)
            try {
                const response = await getPartnerAccruals()
                setItems(response.data)
                setEmptyLabel("Нет данных о начислениях")
            } catch (error) {
                const knownError = error as AxiosError
                setItems([])
                setEmptyLabel(
                    knownError.response?.status === 424
                        ? "Начисления доступны только для сообщества типа «партнёр»"
                        : "Не удалось загрузить начисления"
                )
                console.error('Error loading partner accruals:', error)
            } finally {
                setIsLoading(false)
            }
        }

        load()
    }, [])

    return (
        <div className="_page_container">
            <PageHeader title="Бонусы" />
            <div className="flexbox">
                <PageSubtitle title="Начисления" />
                {Boolean(items.length) &&
                    <div className="table_box">
                        <Table columns={columns} data={items} />
                    </div>
                }
                {(!Boolean(items.length) && isLoading) &&
                    <div className="info_panel">
                        <Loader />
                    </div>
                }
                {(!Boolean(items.length) && !isLoading) &&
                    <NoTableData label={emptyLabel} />
                }
            </div>
        </div>
    )
}
